import { Holdings } from '@/api/fundHoldings/models/Holdings';
import { Portfolios } from '@/api/portfolios';
import { PortfolioGroup } from '@/api/portfolios/models/PortfolioGroup';

/**
 * Find the preferred portfolio group ID to be the default portfolio group shown
 * in the dashboard.
 *
 * Selection priority:
 * 1. Groups with holdings, sorted by latest performance date (most recent first)
 * 2. All groups sorted by latest performance date (most recent first)
 * 3. The first group
 *
 * @returns The preferred portfolio group ID.
 */
export const findPreferredPortfolioGroupId = ({
  portfolios,
  holdings,
}: {
  portfolios: Portfolios;
  holdings: Holdings | undefined;
}): string => {
  if (portfolios.portfolioGroups.length < 2) {
    // If there's only one group, return it regardless of performance data or holdings.
    return portfolios.portfolioGroups[0]!.id;
  }

  const portfolioGroups = portfolios.portfolioGroups ?? [];
  const holdingsById = holdings?.byPortfolioId ?? {};

  const portfolioGroupsWithHoldings = portfolioGroups.filter((pg) =>
    pg.portfolios.some((portfolio) => {
      const h = holdingsById[portfolio.id];
      return h && h.holdings.length > 0;
    }),
  );

  if (portfolioGroupsWithHoldings.length > 0) {
    // If there are groups with holdings, prefer those, sorted by latest performance date.
    const sortedWithHoldings = [...portfolioGroupsWithHoldings].sort(
      byLatestPerformanceDateDesc,
    );
    return sortedWithHoldings[0]!.id;
  }

  // Otherwise, prefer groups with the most recent performance data.
  const groupsWithPerformanceDataSorted = [...portfolioGroups].sort(
    byLatestPerformanceDateDesc,
  );
  return groupsWithPerformanceDataSorted[0]!.id;
};

/** Sort comparator: most recent performance date first, nulls last. */
const byLatestPerformanceDateDesc = (
  a: PortfolioGroup,
  b: PortfolioGroup,
): number => {
  const dateA = a.latestPerformanceDate;
  const dateB = b.latestPerformanceDate;
  if (dateA === null && dateB === null) {
    return 0;
  }
  if (dateA === null) {
    return 1;
  }
  if (dateB === null) {
    return -1;
  }
  return dateB - dateA;
};
